import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, Send, FlaskConical } from "lucide-react";
import api from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { EmailPreview } from "./AdminEmailDialog";
import GroupRunProgress from "./GroupRunProgress";
import { isFilled, LANGS } from "./GroupVersionEditor";

const btn = "px-3 py-2 rounded-md text-xs border flex items-center gap-1.5 disabled:opacity-40 transition-colors";

function useRunPoller(onDone) {
  const [run, setRun] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const follow = (id) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/group-email/runs/${id}`);
        setRun(data);
        if (data.status === "completed") { clearInterval(timer.current); onDone(data); }
      } catch { clearInterval(timer.current); onDone(null); }
    }, 1500);
  };
  return { run, setRun, follow };
}

function Distribution({ counts, t }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="group-distribution">
      <span className="text-slate-500">{t("groupEmail.distribution")}:</span>
      {Object.entries(counts).map(([l, n]) => (
        <span key={l} className="px-1.5 py-0.5 rounded border border-slate-700 font-mono-data text-slate-300" data-testid={`group-dist-${l}`}>{l.toUpperCase()} {n}</span>
      ))}
    </div>
  );
}

export default function GroupSendPanel({ versions, lang, users, selected, testEmail, onRunDone, t }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { run, setRun, follow } = useRunPoller((data) => {
    setBusy(""); onRunDone();
    if (data) toast.success(t("groupEmail.runDone", { sent: data.sent, failed: data.failed }));
  });
  useEffect(() => { setPreview(null); }, [versions, lang]);

  const filled = LANGS.filter((l) => isFilled(versions[l]));
  const counts = useMemo(() => {
    const out = {};
    users.filter((u) => selected.has(u.id)).forEach((u) => {
      const l = filled.includes(u.lang) ? u.lang : "en";
      out[l] = (out[l] || 0) + 1;
    });
    return out;
  }, [users, selected, filled.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = (extra) => ({ versions: Object.fromEntries(filled.map((l) => [l, versions[l]])), ...extra });
  const errMsg = (e) => e.response?.data?.detail || t("admin.emailNetworkError");
  const call = async (label, fn) => {
    setBusy(label); setError("");
    try { await fn(); } catch (e) { setError(errMsg(e)); setBusy(""); return; }
    if (label !== "send") setBusy("");
  };
  const enReady = isFilled(versions.en);
  const langReady = enReady && isFilled(versions[lang]) && !busy;
  const L = lang.toUpperCase();

  const loadPreview = () => call("preview", async () => setPreview((await api.post("/admin/group-email/preview", payload({ test_lang: lang }))).data));
  const sendTest = () => call("test", async () => {
    const { data } = await api.post("/admin/group-email/send", payload({ test_lang: lang }));
    toast.success(t("groupEmail.testSent", { to: data.recipient }));
  });
  const sendAll = () => call("send", async () => {
    setConfirmOpen(false);
    const { data } = await api.post("/admin/group-email/send", payload({ user_ids: [...selected], confirm: true }));
    setRun({ id: data.id, total: data.total, progress: 0, sent: 0, failed: 0, status: "running", results: [] });
    follow(data.id);
  });

  return (
    <div className="space-y-4">
      <div className="tech-card p-4 space-y-3" data-testid="group-send-panel">
        <div className="label-tech text-slate-400">{t("groupEmail.sendTitle")}</div>
        <Distribution counts={counts} t={t} />
        {!enReady && <div className="text-xs text-amber-300" data-testid="group-need-en">{t("groupEmail.needEn")}</div>}
        {error && <div role="alert" className="text-sm text-red-300 border border-red-500/40 bg-red-500/10 rounded-md px-3 py-2" data-testid="group-error">{error}</div>}
        <div className="flex flex-wrap gap-2">
          <button onClick={loadPreview} disabled={!langReady} className={`${btn} border-slate-600 text-slate-200 hover:border-amber-500`} data-testid="group-preview-btn">
            {busy === "preview" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}{t("groupEmail.previewBtn", { lang: L })}
          </button>
          <button onClick={sendTest} disabled={!langReady} className={`${btn} border-slate-600 text-slate-200 hover:border-amber-500`} data-testid="group-test-btn">
            {busy === "test" ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}{t("groupEmail.testBtn", { lang: L, email: testEmail || "…" })}
          </button>
          <button onClick={() => setConfirmOpen(true)} disabled={!enReady || !selected.size || !!busy}
            className={`${btn} sm:ml-auto border-amber-500 bg-amber-500 text-slate-950 font-semibold hover:bg-amber-400`} data-testid="group-send-btn">
            {busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}{t("groupEmail.sendBtn", { n: selected.size })}
          </button>
        </div>
        {preview && <><div className="text-[11px] text-slate-500">{t("groupEmail.previewNote")}</div><EmailPreview preview={preview} t={t} /></>}
      </div>
      {run && <GroupRunProgress run={run} t={t} />}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-slate-950 border-slate-800 text-slate-100" data-testid="group-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("groupEmail.confirmTitle", { n: selected.size })}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{t("groupEmail.confirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <Distribution counts={counts} t={t} />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-slate-100" data-testid="group-confirm-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={sendAll} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="group-confirm-send">{t("groupEmail.confirmSend")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
